import { Action, BatchUploadType, InvoiceStatus, Plan, RemindingTone, ScheduledAction } from '../api/gql';
import { theme } from '../style/theme';

export const getTheOldestScheduledAction = (actions: Partial<ScheduledAction>[]): Partial<ScheduledAction> | null => {
  if (!actions.length) {
    return null;
  }

  const sorted = actions.sort((a, b) => {
    if (a.scheduledAt && b.scheduledAt) {
      return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
    }
    return 0;
  });

  return sorted[0];
};

export const getPlanTransKey = (plan: Plan) => {
  const map: Record<Plan, string> = {
    [Plan.Standard]: 'plan.standard',
    [Plan.Trial]: 'plan.trial',
  };

  return map[plan];
};

export const getActionTransKey = (action: Action) => {
  const map: Record<Action, string> = {
    [Action.Email]: 'actionEmail',
    [Action.Sms]: 'actionSms',
    [Action.Call]: 'actionCall',
  };

  return map[action];
};

export const getRemindingToneTransKey = (tone: RemindingTone) => {
  const map: Record<RemindingTone, string> = {
    [RemindingTone.Polite]: 'tone.polite.title',
    [RemindingTone.Neutral]: 'tone.neutral.title',
    [RemindingTone.Firm]: 'tone.firm.title',
  };
  return map[tone];
};

export const getInvoiceStateProperties = (
  invoiceState: InvoiceStatus,
  invoiceId: string,
  batchId?: string,
  batchType?: BatchUploadType,
  isAnnotated?: boolean,
) => {
  if (batchId && invoiceState === InvoiceStatus.AwaitingPayment) {
    return {
      transKey: 'state.remindingNotStarted',
      color: theme.palette.warning.main,
      route: batchType === BatchUploadType.Pdf ? `/upload/batch/${batchId}` : `/upload/batch/${batchId}/billing`,
    };
  }

  const map: Record<InvoiceStatus, { transKey: string; color: string; route: string }> = {
    [InvoiceStatus.Processing]: {
      transKey: 'state.processing',
      color: theme.palette.grey['700'],
      route: `/upload/invoice/${invoiceId}/annotation-loading`,
    },
    [InvoiceStatus.MissingRequiredData]: {
      transKey: 'state.processing',
      color: theme.palette.grey['700'],
      route: `/upload/invoice/${invoiceId}/confirm`,
    },
    [InvoiceStatus.AwaitingVerification]: {
      transKey: 'state.awaitingVerification',
      color: theme.palette.info.main,
      route: `/upload/invoice/${invoiceId}/confirm`,
    },
    [InvoiceStatus.AwaitingPayment]: {
      transKey: 'state.remindingNotStarted',
      color: theme.palette.warning.main,
      route: `/upload/invoice/${invoiceId}/billing`,
    },
    [InvoiceStatus.Settled]: {
      transKey: 'state.paid',
      color: theme.palette.success.main,
      route: `/invoice/${invoiceId}`,
    },
    [InvoiceStatus.Reminding]: {
      transKey: 'state.reminding',
      color: theme.palette.warning.main,
      route: `/invoice/${invoiceId}`,
    },
    [InvoiceStatus.Complete]: {
      transKey: 'state.completed',
      color: theme.palette.error.main,
      route: `/invoice/${invoiceId}`,
    },
    [InvoiceStatus.Archived]: {
      transKey: 'state.archived',
      color: theme.palette.grey['400'],
      route: `/invoice/${invoiceId}`,
    },
  };

  // if it's batch upload before payment route to batch summary page
  if (
    batchId &&
    [
      InvoiceStatus.Processing,
      InvoiceStatus.MissingRequiredData,
      InvoiceStatus.AwaitingVerification,
      InvoiceStatus.AwaitingPayment,
    ].includes(invoiceState)
  ) {
    return {
      transKey: map[invoiceState].transKey,
      color: map[invoiceState].color,
      route: `/upload/batch/${batchId}`,
    };
  }

  // manually created invoice flow
  if (
    !batchId &&
    !isAnnotated &&
    [InvoiceStatus.Processing, InvoiceStatus.MissingRequiredData, InvoiceStatus.AwaitingVerification].includes(
      invoiceState,
    )
  ) {
    return {
      transKey: map[invoiceState].transKey,
      color: map[invoiceState].color,
      route: `/new/invoice/${invoiceId}`,
    };
  }

  return map[invoiceState];
};
